import { Button } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { type Accept, type FileWithPath, useDropzone } from "react-dropzone";

import style from "./DropArea.module.css";

interface DropZoneProps {
  maxFilesReached?: boolean;
  disabled?: boolean;
  maxFiles: number;
  maxFileSizeInMB: number;
  acceptedFileExtensions: string[];
  onChange: (files: FileWithPath) => void;
}

const DropArea: React.FC<DropZoneProps> = ({
  maxFiles,
  maxFileSizeInMB,
  acceptedFileExtensions,
  disabled,
  maxFilesReached,
  onChange,
}) => {
  const isDisabled = disabled || maxFilesReached;
  const accept: Accept = {
    "application/octet-stream": acceptedFileExtensions.map((ext) => `.${ext}`),
  };

  const { acceptedFiles, fileRejections, isDragActive, open, getRootProps, getInputProps } =
    useDropzone({
      accept,
      maxSize: maxFileSizeInMB * 1024 * 1024,
      multiple: false,
      noClick: true,
      noKeyboard: true,
      disabled: isDisabled,
      onDropAccepted: (files) => {
        onChange(files[0]);
      },
      onDragOver: () => void 0,
      onDragLeave: () => void 0,
      onDragEnter: () => void 0,
    });

  const getTopText = () => {
    if (maxFilesReached) return `A maximum of ${maxFiles} files are already uploaded`;
    if (acceptedFiles.length) return "Upload successful";
    return "Drag here a file to upload, or";
  };

  const getErrorMessage = () => {
    const error = fileRejections?.[0]?.errors?.[0];
    if (isDisabled || !error) return undefined;

    switch (error.code) {
      case "file-too-large": {
        return `Error - file is bigger than ${maxFileSizeInMB}MB`;
      }
      case "file-invalid-type": {
        return `Error - file type must be ${acceptedFileExtensions.join(", ")}`;
      }
      case "too-many-files": {
        return `Error - pick one file at a time`;
      }
      default: {
        return "Error - unknown error";
      }
    }
  };

  return (
    <div
      {...getRootProps({
        className: classNames(
          style.dropZone,
          { [style.dragActive]: isDragActive },
          { [style.disabled]: isDisabled }
        ),
      })}
    >
      <input data-testid={"drop-area-input"} {...getInputProps()} />
      {!isDisabled && isDragActive && <p>Release file to upload</p>}
      {!isDragActive && (
        <>
          <p className={style.dropZoneText}>{getTopText()}</p>
          <Button type="button" variant="grayscale" onClick={open} disabled={isDisabled}>
            Browse files...
          </Button>
          {!isDragActive && <p className={style.errorText}>{getErrorMessage()}</p>}
        </>
      )}
    </div>
  );
};

export default DropArea;
